/**
 * ThemeToggle — botón icono que cicla light → dark → system → light.
 *
 * Estado:
 *   - Icono: Sun (light) / Moon (dark) / Monitor (system)
 *   - aria-label: refleja el tema actual para lectores de pantalla
 *   - title: hint de siguiente estado al hacer hover
 */

import { Monitor, Moon, Sun } from 'lucide-react';
import { type ComponentType, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';
import { useTheme, type Theme } from '../lib/theme.js';

const NEXT: Record<Theme, Theme> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const ICON: Record<Theme, ComponentType<{ className?: string }>> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const LABEL: Record<Theme, string> = {
  light: 'Tema claro',
  dark: 'Tema oscuro',
  system: 'Tema del sistema',
};

export const ThemeToggle = ({
  className,
}: {
  className?: string;
}): ReactElement => {
  const { theme, setTheme } = useTheme();
  const Icon = ICON[theme];
  const next = NEXT[theme];
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      className={cn(
        'flex size-9 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
      aria-label={LABEL[theme]}
      title={`${LABEL[theme]} — click para ${LABEL[next].toLowerCase()}`}
    >
      <Icon className="size-4" />
    </button>
  );
};
